import { useState } from "react";
import { PaperPlaneRight, CircleNotch, X, ChatCircleText, PencilSimple } from "@phosphor-icons/react";
import api from "@/lib/api";
import MicButton from "@/components/MicButton";

// A single always-present input docked over the canvas: ask about OR revise the areas / needs / tensions.
export default function CommandBar({ sessionId, disabled, onChanged }) {
  const [open, setOpen] = useState(false);
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const [reply, setReply] = useState(null);

  const send = async () => {
    const q = text.trim();
    if (!q || busy) return;
    setBusy(true);
    setReply(null);
    try {
      const r = await api.commandAreas(sessionId, q);
      setReply({ q, a: r.reply, changed: r.changed });
      setText("");
      if (r.changed) await onChanged?.();
    } catch (e) {
      setReply({ q, a: e?.response?.data?.detail || "Something went wrong — try again." });
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="pointer-events-none fixed inset-x-0 bottom-0 z-40 flex justify-center px-4 pb-12">
      <div className="pointer-events-auto w-full max-w-2xl">
        {reply && open && (
          <div data-testid="command-reply" className="mb-2 border border-border bg-card/95 p-3.5 shadow-lg backdrop-blur">
            <div className="mb-1 flex items-start justify-between gap-2">
              <p className="text-xs italic text-muted-foreground">“{reply.q}”</p>
              <button onClick={() => setReply(null)} className="text-muted-foreground hover:text-foreground"><X size={14} /></button>
            </div>
            {reply.changed && (
              <div data-testid="command-changed" className="mb-1.5 inline-flex items-center gap-1 text-[11px] font-medium" style={{ color: "hsl(var(--tag-preference))" }}>
                <PencilSimple size={12} weight="bold" /> Problem space updated
              </div>
            )}
            <p className="text-sm leading-relaxed text-foreground">{reply.a}</p>
          </div>
        )}
        {open ? (
          <div className="border border-foreground/20 bg-card/95 p-2.5 shadow-xl backdrop-blur">
            <div className="mb-2 flex items-center justify-between px-1">
              <span className="overline">Ask a question or change the problem space</span>
              <button data-testid="command-close" onClick={() => setOpen(false)} className="text-muted-foreground hover:text-foreground"><X size={15} /></button>
            </div>
            <div className="flex items-center gap-2">
              <input
                data-testid="command-input"
                autoFocus
                value={text}
                onChange={(e) => setText(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && send()}
                placeholder="e.g. “Why is X a problem?” · “Add a problem about…” · “Rename…” · “Remove…”"
                className="flex-1 bg-transparent px-3 py-2 text-sm outline-none placeholder:text-muted-foreground"
              />
              <MicButton size={17} className="ml-1" onResult={(t) => setText((p) => (p ? p.trim() + " " + t : t))} />
              <button
                data-testid="command-send"
                onClick={send}
                disabled={busy || !text.trim()}
                className="inline-flex items-center gap-1.5 rounded-sm bg-primary px-3 py-2 text-sm font-medium text-primary-foreground transition-colors hover:bg-primary/85 disabled:opacity-40"
              >
                {busy ? <CircleNotch size={15} className="animate-spin" /> : <PaperPlaneRight size={15} weight="bold" />}
              </button>
            </div>
            <div className="mt-2 flex flex-wrap gap-1.5 px-1">
              {[
                ["Ask", "Why is this a problem? "],
                ["Add a problem", "Add a problem about "],
                ["Revise", "Revise the problem about "],
                ["Remove", "Remove the problem about "],
              ].map(([label, hint]) => (
                <button key={label} onClick={() => setText((t) => (t ? t : hint))} className="rounded-sm border border-border px-2 py-0.5 text-[11px] text-muted-foreground transition-colors hover:border-foreground/40 hover:text-foreground">{label}</button>
              ))}
            </div>
          </div>
        ) : (
          <div className="flex flex-col items-center gap-1.5">
            <span className="rounded-full bg-background/80 px-2.5 py-0.5 text-[11px] font-medium uppercase tracking-wide text-muted-foreground shadow-sm backdrop-blur">Your turn — question or change this</span>
            <button
              data-testid="command-open"
              onClick={() => setOpen(true)}
              disabled={disabled}
              className="group mx-auto flex items-center gap-2.5 rounded-full px-6 py-3 text-[15px] font-semibold text-white shadow-xl ring-1 ring-black/10 transition-transform hover:scale-[1.03] disabled:opacity-40"
              style={{ background: "hsl(var(--tag-preference))" }}
            >
              <span className="relative flex h-2.5 w-2.5">
                <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-white/70" />
                <span className="relative inline-flex h-2.5 w-2.5 rounded-full bg-white" />
              </span>
              <ChatCircleText size={19} weight="fill" /> Ask a question · add, revise, or remove a problem
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
